import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
import PracticesAPI from 'api/PracticesAPI'
import UsersAPI from 'api/UsersAPI'
import PatientAPI from 'api/PatientAPI'
import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
import createConfig from '../i18n/locales/en/pages/practice/create.js'
import { buildProposeTimeQuery, getProposeTimeUserId } from '@/utils/referralPoolProposeFlow'

export default {
    data() {
        return {
            referral: null,
            locations: [],
            selectedReferralConstraint: {},
            isReasonCodeModalOpen: false,
            isPracticeCreationOpen: false,
            showAvailableLocationsModal: false,
            isFlowInitializing: false,
            isFlowReady: false,
        }
    },

    computed: {
        reasonKey() {
            const reasonCode = this.selectedReferralConstraint?.reasonCode
            if (!reasonCode) return 'DEFAULT'
            if (typeof reasonCode === 'string') return reasonCode
            if (typeof reasonCode === 'object') return reasonCode.code || 'DEFAULT'
            return 'DEFAULT'
        },
        reasonConfig() {
            return createConfig.reason_codes[this.reasonKey] || createConfig.reason_codes.DEFAULT
        },
        reasonMessage() {
            return this.selectedReferralConstraint?.reasonMessage || this.reasonConfig.message
        },
    },

    methods: {
        buildProposeTimeQuery(referral, locationId, distance) {
            return buildProposeTimeQuery(this.$auth, referral, locationId, distance)
        },

        async loadScheduleContext() {
            const query = this.$route.query

            if (query.isFromPatient && query.patientId) {
                this.patient = await PatientAPI.getPatientDetails(query.patientId, this.$auth.getUser().id)
                return
            }

            if (query.practiceType === PracticeTypeEnum.PHYSICIAN) {
                const userId = query.userId || getProposeTimeUserId(this.$auth)
                this.physician = await UsersAPI.get(userId)
                return
            }

            if (query.practiceId) {
                this.practice = await PracticesAPI.getPracticeById(query.practiceId)
            }
        },

        async finishProposeTimeFlow(referral, locationId, distance) {
            await this.applyProposeTimeRoute(referral, locationId, distance)
            await this.loadScheduleContext()
            this.isFlowReady = true
        },

        hasReasonCode(referral) {
            const reasonCode = referral?.reasonCode
            if (reasonCode === null || reasonCode === undefined) return false
            if (typeof reasonCode === 'string') return reasonCode.trim().length > 0
            if (typeof reasonCode === 'object') return Object.keys(reasonCode).length > 0
            return true
        },

        async initProposeTimeFlow() {
            if (this.isFlowReady || this.isFlowInitializing) return

            this.isFlowInitializing = true
            try {
                const poolId = this.$route.params.poolId
                if (this.hasReasonCode(referral)) {
                    const result = await ReferralsPoolAPI.promoteConstrained(poolId)
                    if (result?.promoted === false) {
                        this.selectedReferralConstraint = result
                        this.isReasonCodeModalOpen = true
                        return
                    }
                }

                const referralType = this.$route.query.referralType || 'received'
                const activeUserId = this.$auth.isPracticeAncillary()
                    ? null
                    : this.$auth.getActiveMember()?.id || this.$auth.getUser().id
                const practiceId = this.$auth.isPracticeAncillary() ? this.$auth.getPractice()?.id : null
                const referral = await ReferralsPoolAPI.getReferralPoolById(poolId, referralType, activeUserId, practiceId)
                this.referral = referral

                await PracticesAPI.acceptInsuranceAndTests(
                    this.$auth.getPractice().id,
                    [referral.insurancePlanId],
                    referral.referralTests,
                )

                if (this.$route.query.pendingInit !== '1') {
                    await this.finishProposeTimeFlow(referral, Number(this.$route.params.locationId))
                    return
                }

                if (referral.toPracticeLocationId) {
                    await this.finishProposeTimeFlow(referral, referral.toPracticeLocationId)
                    return
                }

                const locationQuery = {
                    address: referral.searchBy?.address || undefined,
                    city: referral.searchBy?.city || undefined,
                    state: referral.searchBy?.state || undefined,
                    zip: referral.searchBy?.zip || undefined,
                    patientId: referral.patientId,
                    referralPoolId: referral.id,
                }
                const activeMemberId = this.$auth.getActiveMember()?.id
                this.locations = await (this.$auth.isPracticeAncillary()
                    ? ReferralsPoolAPI.getAncillaryAvailableLocations(this.$auth.getPractice().id, locationQuery)
                    : ReferralsPoolAPI.getAvailableLocations(activeMemberId, locationQuery))

                if (!this.locations.length) {
                    this.$router.back()
                    return
                }

                if (this.locations.length > 1) {
                    this.showAvailableLocationsModal = true
                    return
                }

                const locationId = this.$auth.getPractice().practiceType === PracticeTypeEnum.ANCILLARY ? 0 : this.locations[0].id
                await this.finishProposeTimeFlow(referral, locationId, this.locations[0]?.distanceToPatient)
            } catch (e) {
                this.$router.back()
            } finally {
                this.isFlowInitializing = false
            }
        },

        applyProposeTimeRoute(referral, locationId, distance) {
            return this.$router.replace({
                name: 'referrals.pool.propose.time',
                params: { poolId: referral.id, locationId },
                query: {
                    referralType: this.$route.query.referralType || 'received',
                    ...this.buildProposeTimeQuery(referral, locationId, distance),
                },
            })
        },

        async onProposeLocationSelected(event) {
            this.showAvailableLocationsModal = false
            const locationId = event.practiceLocationId ?? event.id
            await this.finishProposeTimeFlow(this.referral, locationId, event.distance)
        },

        onProposeLocationsModalClose() {
            this.showAvailableLocationsModal = false
            if (!this.isFlowReady) {
                this.$router.back()
            }
        },

        closeReasonCodeModal() {
            this.selectedReferralConstraint = {}
            this.isReasonCodeModalOpen = false
            this.$router.back()
        },

        runReasonAction() {
            if (!this.reasonConfig.action) return
            if (this.reasonConfig.action === 'GO_TO_PROFILE') {
                this.$router.push({ name: 'practice.profile' })
                return
            }
            if (this.reasonConfig.action === 'GO_TO_BILLING') {
                this.$router.push({ name: 'practice.billing' })
                return
            }
            if (this.reasonConfig.action === 'GO_TO_PRACTICE_CREATE') {
                this.togglePracticeCreationModal()
            }
        },

        togglePracticeCreationModal() {
            this.isPracticeCreationOpen = !this.isPracticeCreationOpen
        },

        goToCreatePractice() {
            this.isPracticeCreationOpen = false
            this.$router.push({
                name: 'company.create',
                query: { redirect: this.$route.fullPath },
            })
        },
    },
}
